import {
    collection,
    deleteField,
    doc,
    DocumentData,
    documentId,
    FieldPath,
    getDoc,
    getDocs,
    orderBy,
    query,
    setDoc,
    updateDoc,
    where,
} from 'firebase/firestore';

import { db } from 'external/firebase/index';

import { chunkArray, formatErrorMessage } from './utils';

export const getUserProfile = async (userUid: string) => {
    try {
        const docRef = doc(db, 'users', userUid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        }
        return { ok: false as const, error: { code: 'db/no-document' } };
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getGroupIdByGroupCode = async (groupCode: string) => {
    try {
        let groupId;
        const q = query(collection(db, 'groups'), where('code', '==', groupCode));
        const querySnap = await getDocs(q);
        querySnap.forEach(docSnap => {
            groupId = docSnap.id;
        });
        return groupId;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getGroup = async (group: string) => {
    try {
        const docRef = doc(db, 'groups', group);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            throw new Error('db/no-document');
        }
        return docSnap.data();
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getGroups = async () => {
    try {
        const groups: DocumentData[] = [];
        const q = query(collection(db, 'groups'));
        const querySnap = await getDocs(q);
        querySnap.forEach(docSnap => {
            groups.push({ id: docSnap.id, ...docSnap.data() });
        });
        return groups;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

// Sets each module's unlock value; an empty value removes the module's entry, which hides it.
export const updateGroupModules = async (group: string, modules: Record<string, string>) => {
    try {
        const docRef = doc(db, 'groups', group);
        const fieldsAndValues = Object.entries(modules).flatMap(([moduleId, value]) => [
            new FieldPath('modules', moduleId),
            value.trim() ? value.trim() : deleteField(),
        ]);
        if (fieldsAndValues.length === 0) {
            return { ok: true } as { ok: true };
        }
        const [firstField, firstValue, ...rest] = fieldsAndValues;
        await updateDoc(docRef, firstField as FieldPath, firstValue, ...rest);
        return { ok: true } as { ok: true };
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getGroupCoursesIds = async (group: string) => {
    try {
        const docRef = doc(db, 'groups', group);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            throw new Error('db/no-document');
        }
        return docSnap.data().courses;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

// Lecturer/curator assignment and group-field writes used to live here as direct
// client-SDK writes. Firestore rules restrict `users/{uid}` to the document owner
// and `groups` to isAdmin(), so an admin assigning someone else was always denied.
// They now run server-side via assignGroupLecturers / assignGroupCurator in
// controllers/students.ts, where the Admin SDK bypasses rules.

export interface RoleUser {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    group?: unknown;
}

const normalizeRoleGroup = (currentGroup: unknown, role: string): string | string[] => {
    if (Array.isArray(currentGroup)) {
        const withoutRole = currentGroup.filter(item => item !== role);
        return [role, ...withoutRole];
    }
    if (typeof currentGroup === 'string') {
        if (currentGroup === role) {
            return currentGroup;
        }
        if (!currentGroup.trim()) {
            return role;
        }
        return [role, currentGroup];
    }
    return role;
};

export const getUsersByRole = async (role: string): Promise<RoleUser[] | { ok: false; code: string }> => {
    try {
        const usersRef = collection(db, 'users');
        const [roleArraySnap, roleStringSnap] = await Promise.all([
            getDocs(query(usersRef, where('group', 'array-contains', role))),
            getDocs(query(usersRef, where('group', '==', role))),
        ]);

        const byId = new Map<string, RoleUser>();

        roleArraySnap.forEach(docSnap => {
            byId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });

        roleStringSnap.forEach(docSnap => {
            byId.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });

        return Array.from(byId.values());
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const assignRoleToUserByEmail = async (
    role: 'admin' | 'dėstytojas' | 'kuratorius',
    firstName: string,
    lastName: string,
    email: string,
) => {
    try {
        const normalizedEmail = email.trim().toLowerCase();
        const usersRef = collection(db, 'users');
        const querySnap = await getDocs(query(usersRef, where('email', '==', normalizedEmail)));

        if (querySnap.empty) {
            throw new Error('auth/user-not-found');
        }

        const userDoc = querySnap.docs[0];
        const currentGroup = userDoc.data()?.group;
        const nextGroup = normalizeRoleGroup(currentGroup, role);

        await updateDoc(userDoc.ref, {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: normalizedEmail,
            group: nextGroup,
        });

        return { ok: true } as { ok: true };
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getPublicModules = async () => {
    try {
        const modules: DocumentData[] = [];
        const q = query(collection(db, 'modules'), where('public', '==', true));
        const querySnap = await getDocs(q);
        querySnap.forEach(docSnap => {
            modules.push({ id: docSnap.id, ...docSnap.data() });
        });
        modules.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
        return modules;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getModule = async (module: string) => {
    try {
        const docRef = doc(db, 'modules', module);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            throw new Error('db/no-document');
        }
        return docSnap.data();
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getCourse = async (course: string) => {
    try {
        const docRef = doc(db, 'courses', course);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            throw new Error('db/no-document');
        }
        return docSnap.data();
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getModules = async (moduleIds: string[]) => {
    try {
        const modules: DocumentData[] = [];

        // Split the moduleIds into chunks of 10
        const chunks = chunkArray(moduleIds, 10);

        // Use Promise.all to query all chunks in parallel
        const results = await Promise.all(
            chunks.map(chunk => {
                const q = query(collection(db, 'modules'), where(documentId(), 'in', chunk));
                return getDocs(q);
            }),
        );

        results.forEach(querySnap => {
            querySnap.forEach(docSnap => {
                modules.push({ id: docSnap.id, ...docSnap.data() });
            });
        });

        modules.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));

        return modules;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getAllCourses = async () => {
    try {
        const courses: DocumentData[] = [];
        const q = query(collection(db, 'courses'));
        const querySnap = await getDocs(q);
        querySnap.forEach(docSnap => {
            courses.push({ id: docSnap.id, ...docSnap.data() });
        });
        courses.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
        return courses;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getCourses = async (courseIds: string[]) => {
    try {
        const courses: DocumentData[] = [];

        // Split the courseIds into chunks of 10
        const chunks = chunkArray(courseIds, 10);

        // Use Promise.all to query all chunks in parallel
        const results = await Promise.all(
            chunks.map(chunk => {
                const q = query(collection(db, 'courses'), where(documentId(), 'in', chunk));
                return getDocs(q);
            }),
        );

        results.forEach(querySnap => {
            querySnap.forEach(docSnap => {
                courses.push({ id: docSnap.id, ...docSnap.data() });
            });
        });

        courses.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));

        return courses;
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getModuleLesson = async (module: string, lesson: string) => {
    try {
        // TODO: Change "topics" to "lessons" once DB migrations is done
        const docRef = doc(db, 'modules', module, 'topics', lesson);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
            throw new Error('db/no-document');
        }
        return docSnap.data();
    } catch (error) {
        return formatErrorMessage(error);
    }
};

export const getGroupModuleLastLessonNumber = async (group: string, module: string) => {
    if (typeof group !== 'string' || !group) {
        return 0;
    }
    const docRef = doc(db, 'groups', group);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        throw new Error('db/no-document');
    }

    const openModuleLessons = docSnap.data().modules?.[module];

    if (openModuleLessons === undefined || openModuleLessons === null || openModuleLessons === '') {
        return 0;
    }

    // eslint-disable-next-line no-extra-boolean-cast
    return !!Number(openModuleLessons)
        ? Number(openModuleLessons)
        : String(openModuleLessons)
              .split(',')
              .filter(n => n.trim() !== '')
              .map(n => Number(n));
};

export const getGroupAllowedModuleIds = async (group: string): Promise<string[]> => {
    if (typeof group !== 'string' || !group) {
        return [];
    }
    const docRef = doc(db, 'groups', group);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
        return [];
    }
    const modules = docSnap.data().modules ?? {};
    return Object.keys(modules).filter(key => {
        const value = modules[key];
        return value !== undefined && value !== null && String(value).trim() !== '';
    });
};

export const getModuleLessons = async (module: string, lessonNumbers: number | number[]) => {
    const lessons: DocumentData[] = [];
    const docRef = doc(db, 'modules', module);
    if (Array.isArray(lessonNumbers)) {
        // Split the moduleIds into chunks of 10
        const chunks = chunkArray(lessonNumbers, 10);

        // Use Promise.all to query all chunks in parallel
        const results = await Promise.all(
            chunks.map(chunk => {
                const q = query(collection(docRef, 'topics'), where('order', 'in', chunk));
                return getDocs(q);
            }),
        );

        results.forEach(querySnap => {
            querySnap.forEach(docSnap => {
                lessons.push({ id: docSnap.id, ...docSnap.data() });
            });
        });
        const orderedLessons = lessonNumbers
            .map(order => lessons.find(lesson => lesson.order === order))
            .filter(lesson => lesson);
        return orderedLessons;
    }
    const q = query(collection(docRef, 'topics'), where('order', '<=', lessonNumbers), orderBy('order'));
    const querySnap = await getDocs(q);
    querySnap.forEach(docSnap => {
        lessons.push({ id: docSnap.id, ...docSnap.data() });
    });
    return lessons;
};
